import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Filter, Upload, X, FileText, Loader2, RotateCcw, ChevronLeft, ChevronRight, Calendar, Home, Download, FileArchive, Paperclip, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { financeiroService, ContaReceber, ContratoAberto, calcularRecebimentosPrevistos, RecebimentoPrevisto } from '@/services/financeiroService';
import { useInterBankSyncPoller } from '@/hooks/useInterBankSyncPoller';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import JSZip from 'jszip';
import { getCurrentDateLocal, parseDateLocal, ordenarContasReceber, type ColunaOrdenacaoContaReceber, type SortDirection } from './contasPagarUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEmpresaFinanceira } from '@/contexts/EmpresaFinanceiraContext';
import { ContaDocumentacaoBadge } from './ContaDocumentacaoBadge';
import { configuracoesFinanceiroService, ConfiguracoesFinanceiro } from '@/services/configuracoesFinanceiroService';

export function ContasReceber() {
  const isMobile = useIsMobile();
  const { empresaId, exigirEmpresa } = useEmpresaFinanceira();
  const [contas, setContas] = useState<ContaReceber[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletarOpcao, setDeletarOpcao] = useState<'apenas' | 'todas' | 'futuras'>('apenas');
  const [contaSelecionada, setContaSelecionada] = useState<ContaReceber | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [busca, setBusca] = useState('');
  const [comprovanteDialogOpen, setComprovanteDialogOpen] = useState(false);
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null);
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [comprovanteFileForm, setComprovanteFileForm] = useState<File | null>(null);
  const [boletoFileForm, setBoletoFileForm] = useState<File | null>(null);
  const [notaFiscalFileForm, setNotaFiscalFileForm] = useState<File | null>(null);
  const [mesAtual, setMesAtual] = useState<string>(() => {
    // Inicializar com o mês mais recente que tem contas, ou o mês atual
    return format(startOfMonth(new Date()), 'yyyy-MM');
  });
  const [popoverAberto, setPopoverAberto] = useState(false);
  const [baixandoDocumentos, setBaixandoDocumentos] = useState(false);
  const [documentosDialogOpen, setDocumentosDialogOpen] = useState(false);
  const [contaParaDocumentos, setContaParaDocumentos] = useState<ContaReceber | null>(null);
  const [editarEscopo, setEditarEscopo] = useState<'apenas' | 'futuras' | 'todas'>('apenas');
  const [editarEscopoDialogOpen, setEditarEscopoDialogOpen] = useState(false);
  const [dadosParaSalvar, setDadosParaSalvar] = useState<Partial<ContaReceber> | null>(null);
  const [contratosAberto, setContratosAberto] = useState<ContratoAberto[]>([]);
  const [carregandoContratos, setCarregandoContratos] = useState(false);
  const [config, setConfig] = useState<ConfiguracoesFinanceiro | null>(null);
  const [sortColumn, setSortColumn] = useState<ColunaOrdenacaoContaReceber>('data_vencimento');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const { refreshCounter: interSyncRefresh, syncStatus: interSyncStatus } = useInterBankSyncPoller();

  // Interface para pagamentos disponíveis para vínculo
  interface PagamentoDisponivel {
    id: string; // contrato_id + etapa
    contrato_id: string;
    contrato_nome: string;
    tipo_contrato?: 'fotovoltaico' | 'carregador';
    etapa: string;
    descricao: string;
    valor: number;
    recebimento: RecebimentoPrevisto;
  }
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data_vencimento: '',
    categoria: '',
    cliente: '',
    contrato_id: '',
    observacoes: '',
    tipo_pagamento: '',
    forma_pagamento: '',
    quantidade_parcelas: undefined as number | undefined,
  });

  useEffect(() => {
    carregarContas();
    configuracoesFinanceiroService.getConfig().then(setConfig);
  }, [filtroStatus, empresaId]);

  const carregarContas = async () => {
    try {
      setLoading(true);
      const filtros: any = {};
      if (filtroStatus !== 'todos') {
        filtros.status = filtroStatus;
      }
      if (empresaId) filtros.empresa_id = empresaId;
      const data = await financeiroService.listarContasReceber(filtros);
      setContas(data);
    } catch (error) {
      console.error('Erro ao carregar contas a receber:', error);
      toast.error('Erro ao carregar contas a receber');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (interSyncRefresh > 0) {
      carregarContas();
      const stats = interSyncStatus?.lastReconcileStats;
      const criadas = stats?.criadasReceber ?? 0;
      const conciliadas = stats?.conciliadasReceber ?? 0;
      if (criadas > 0 && conciliadas > 0) {
        toast.info(
          `Banco Inter: ${conciliadas} conta(s) conciliada(s) e ${criadas} nova(s) importada(s)`
        );
      } else if (conciliadas > 0) {
        toast.info(`${conciliadas} conta(s) a receber conciliada(s) com o Banco Inter`);
      } else if (criadas > 0) {
        toast.info(`${criadas} nova(s) conta(s) a receber importada(s) do Banco Inter`);
      }
    }
  }, [interSyncRefresh]);

  // Função para extrair o contrato_id do ID do pagamento (formato: contrato_id_etapa)
  const extrairContratoId = (pagamentoId: string): string => {
    const etapasPossiveis = ['assinatura', 'inicio', 'conclusao', 'ligacao'];
    for (const etapa of etapasPossiveis) {
      if (pagamentoId.endsWith(`_${etapa}`)) {
        return pagamentoId.slice(0, -(etapa.length + 1));
      }
    }
    return pagamentoId;
  };

  // Função para extrair a etapa do ID do pagamento (formato: contrato_id_etapa). Identificação do vínculo é somente por este campo.
  const extrairEtapa = (pagamentoId: string): string | null => {
    if (!pagamentoId || pagamentoId.trim() === '') return null;
    const etapasPossiveis = ['assinatura', 'inicio', 'conclusao', 'ligacao'];
    for (const etapa of etapasPossiveis) {
      if (pagamentoId.endsWith(`_${etapa}`)) return etapa;
    }
    return null;
  };

  // Verifica se um pagamento (contrato + etapa) já foi vinculado. Critério único: contrato_id + contrato_etapa (não descrição/valor).
  const pagamentoJaVinculado = (contratoId: string, etapa: string, _descricao: string): boolean => {
    return contas.some(conta => {
      if (conta.contrato_id !== contratoId) return false;
      if (conta.contrato_etapa) return conta.contrato_etapa === etapa;
      // Retrocompatibilidade: registros antigos sem contrato_etapa usam descrição
      const descricaoConta = (conta.descricao ?? '').replace(/\s*-\s*Parcela\s+\d+\/\d+.*$/i, '').trim();
      return descricaoConta === _descricao || descricaoConta.includes(_descricao);
    });
  };

  // Função para obter pagamentos disponíveis para vínculo.
  // Inclui sempre o pagamento vinculado à conta em edição (contaSelecionada) para que o campo carregue corretamente ao editar.
  const obterPagamentosDisponiveis = (): PagamentoDisponivel[] => {
    const pagamentos: PagamentoDisponivel[] = [];
    
    contratosAberto.forEach(contrato => {
      const recebimentosPrevistos = calcularRecebimentosPrevistos(contrato);
      
      recebimentosPrevistos.forEach(recebimento => {
        const jaVinculado = pagamentoJaVinculado(contrato.id, recebimento.etapa, recebimento.descricao);
        const ehPagamentoDaContaEmEdicao = contaSelecionada?.contrato_id === contrato.id &&
          (contaSelecionada?.contrato_etapa ? contaSelecionada.contrato_etapa === recebimento.etapa
            : (() => {
                const descricaoConta = (contaSelecionada?.descricao ?? '').replace(/\s*-\s*Parcela\s+\d+\/\d+.*$/i, '').trim();
                return descricaoConta === recebimento.descricao || descricaoConta.includes(recebimento.descricao);
              })());
        // Inclui se não está vinculado a outra conta OU se é o pagamento da conta que estamos editando
        if (!jaVinculado || ehPagamentoDaContaEmEdicao) {
          pagamentos.push({
            id: `${contrato.id}_${recebimento.etapa}`,
            contrato_id: contrato.id,
            contrato_nome: contrato.cliente_nome,
            tipo_contrato: contrato.tipo_contrato,
            etapa: recebimento.etapa,
            descricao: recebimento.descricao,
            valor: recebimento.valor,
            recebimento: recebimento,
          });
        }
      });
    });
    
    return pagamentos;
  };

  const carregarContratosAberto = async (): Promise<ContratoAberto[]> => {
    try {
      setCarregandoContratos(true);
      const contratos = await financeiroService.listarContratosAberto();
      setContratosAberto(contratos);
      return contratos;
    } catch (error) {
      console.error('Erro ao carregar contratos em aberto:', error);
      toast.error('Erro ao carregar contratos em aberto');
      return [];
    } finally {
      setCarregandoContratos(false);
    }
  };

  const abrirDialog = async (conta?: ContaReceber) => {
    // Carregar contratos em aberto quando abrir o dialog e usar o retorno (estado ainda não atualizado)
    const contratosCarregados = await carregarContratosAberto();
    // Recarregar contas para verificar vínculos atualizados
    await carregarContas();
    
    if (conta) {
      setContaSelecionada(conta);
      // Resetar escopo de edição ao abrir diálogo
      setEditarEscopo('apenas');
      
      // Identificação do pagamento é somente pelo campo "Pagamento Previsto do Contrato" (contrato_id + contrato_etapa)
      let contratoIdParaForm = conta.contrato_id || '';
      if (conta.contrato_id && conta.contrato_etapa) {
        contratoIdParaForm = `${conta.contrato_id}_${conta.contrato_etapa}`;
      } else if (conta.contrato_id) {
        // Retrocompatibilidade: registros antigos sem contrato_etapa — inferir etapa por descrição ou valor
        const contrato = contratosCarregados.find(c => c.id === conta.contrato_id);
        if (contrato) {
          const recebimentos = calcularRecebimentosPrevistos(contrato);
          const descricaoConta = conta.descricao.replace(/\s*-\s*Parcela\s+\d+\/\d+.*$/i, '').trim();
          let recebimentoCorrespondente = recebimentos.find(r =>
            descricaoConta === r.descricao || descricaoConta.includes(r.descricao)
          );
          if (!recebimentoCorrespondente && conta.valor != null) {
            const valorConta = typeof conta.valor === 'number' ? conta.valor : Number(conta.valor);
            if (!Number.isNaN(valorConta)) {
              recebimentoCorrespondente = recebimentos.find(r =>
                Math.abs((r.valor || 0) - valorConta) < 0.02
              );
            }
          }
          if (recebimentoCorrespondente) {
            contratoIdParaForm = `${conta.contrato_id}_${recebimentoCorrespondente.etapa}`;
          }
        }
      }
      
      const dadosIniciais = {
        descricao: conta.descricao,
        valor: conta.valor.toString(),
        data_vencimento: conta.data_vencimento,
        categoria: conta.categoria,
        cliente: conta.cliente || '',
        contrato_id: contratoIdParaForm,
        observacoes: conta.observacoes || '',
        tipo_pagamento: (conta.tipo_pagamento || '') as 'avista' | 'parcelado' | '',
        forma_pagamento: conta.forma_pagamento || '',
        quantidade_parcelas: conta.quantidade_parcelas,
      };

      setFormData(dadosIniciais);
      setComprovanteFileForm(null);
      setBoletoFileForm(null);
      setNotaFiscalFileForm(null);
    } else {
      setContaSelecionada(null);
      setFormData({
        descricao: '',
        valor: '',
        data_vencimento: '',
        categoria: '',
        cliente: '',
        contrato_id: '',
        observacoes: '',
        tipo_pagamento: '',
        forma_pagamento: '',
        quantidade_parcelas: undefined,
      });
      setComprovanteFileForm(null); // Resetar arquivo ao criar nova
      setBoletoFileForm(null);
      setNotaFiscalFileForm(null);
    }
    setDialogOpen(true);
  };

  const salvarConta = async () => {
    try {
      if (!formData.descricao || !formData.data_vencimento || !formData.categoria) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }

      // Valor é opcional, mas se informado deve ser válido
      let valor: number | null = null;
      if (formData.valor && formData.valor.trim() !== '') {
        const valorParsed = parseFloat(formData.valor);
        if (isNaN(valorParsed) || valorParsed <= 0) {
          toast.error('Valor inválido');
          return;
        }
        valor = valorParsed;
      }

      // Validação obrigatória para tipo de pagamento parcelado
      if (formData.tipo_pagamento === 'parcelado') {
        if (!formData.quantidade_parcelas || formData.quantidade_parcelas <= 0) {
          toast.error('Informe a quantidade de parcelas');
          return;
        }
      }

      const dataVencimento = parseDateLocal(formData.data_vencimento);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      let status: 'pendente' | 'recebido' | 'vencido' = 'pendente';
      if (dataVencimento < hoje) {
        status = 'vencido';
      }
      // Preservar estado de pagamento ao editar - se estava recebido, deve continuar recebido
      if (contaSelecionada?.status === 'recebido') {
        status = 'recebido';
      }

      let contaId: string;
      if (contaSelecionada) {
        // Edição de conta existente
        if (contaSelecionada.tipo_pagamento === 'parcelado' && contaSelecionada.id_parcelamento && contaSelecionada.numero_parcela) {
          // Edição de parcela
          const quantidadeParcelasAtual = contaSelecionada.quantidade_parcelas || 0;
          const novaQuantidadeParcelas = formData.quantidade_parcelas || quantidadeParcelasAtual;
          
          // Verificar se há alterações
          const descricaoMudou = formData.descricao !== contaSelecionada.descricao;
          const valorMudou = valor !== contaSelecionada.valor;
          const categoriaMudou = formData.categoria !== contaSelecionada.categoria;
          const clienteMudou = (formData.cliente || '') !== (contaSelecionada.cliente || '');
          const contratoIdFormData = formData.contrato_id && formData.contrato_id.trim() !== '' 
            ? extrairContratoId(formData.contrato_id) 
            : '';
          const contratoMudou = contratoIdFormData !== (contaSelecionada.contrato_id || '');
          const observacoesMudaram = (formData.observacoes || '') !== (contaSelecionada.observacoes || '');
          const formaPagamentoMudou = (formData.forma_pagamento || '') !== (contaSelecionada.forma_pagamento || '');
          const dataVencimentoMudou = formData.data_vencimento !== contaSelecionada.data_vencimento;
          
          const haAlteracoes = descricaoMudou || valorMudou || categoriaMudou || clienteMudou || 
                             contratoMudou || observacoesMudaram || formaPagamentoMudou || dataVencimentoMudou;
          
          // Verificar se há arquivos para upload
          const haArquivosParaUpload = comprovanteFileForm || boletoFileForm || notaFiscalFileForm;
          
          // Se há alterações, mostrar diálogo de escopo
          if (haAlteracoes) {
            // Preparar dados para atualização
            const contratoIdParaSalvar = formData.contrato_id && formData.contrato_id.trim() !== '' 
              ? extrairContratoId(formData.contrato_id) 
              : null;
            const contratoEtapaParaSalvar = formData.contrato_id ? extrairEtapa(formData.contrato_id) : null;
            const contratoTipo = contratoIdParaSalvar ? (contratosAberto.find(c => c.id === contratoIdParaSalvar)?.tipo_contrato ?? 'fotovoltaico') : undefined;
            const dadosParaSalvarTemp: Partial<ContaReceber> = {
              descricao: formData.descricao,
              valor: valor!,
              categoria: formData.categoria,
              cliente: formData.cliente && formData.cliente.trim() !== '' ? formData.cliente : null,
              contrato_id: contratoIdParaSalvar,
              contrato_etapa: contratoEtapaParaSalvar,
              contrato_tipo: contratoTipo,
              observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
              forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
              quantidade_parcelas: novaQuantidadeParcelas,
              data_vencimento: formData.data_vencimento,
              status,
              ...(status === 'recebido' && contaSelecionada?.data_recebimento ? { data_recebimento: contaSelecionada.data_recebimento } : {}),
            };

            // Armazenar dados e abrir diálogo de escolha de escopo
            setDadosParaSalvar(dadosParaSalvarTemp);
            setEditarEscopo('apenas'); // Resetar para padrão
            setEditarEscopoDialogOpen(true);
            return; // Não continuar, aguardar escolha do usuário
          }
          
          // Se não há alterações mas há arquivos para upload, fazer upload diretamente
          if (!haAlteracoes && haArquivosParaUpload) {
            // Fazer upload dos arquivos diretamente para a conta selecionada
            if (comprovanteFileForm) {
              try {
                const formDataUpload = new FormData();
                formDataUpload.append('comprovante', comprovanteFileForm);
                const response = await fetch(`/api/contas-financeiro/receber/${contaSelecionada.id}/comprovante`, {
                  method: 'POST',
                  body: formDataUpload,
                });
                if (response.ok) {
                  toast.success('Comprovante enviado com sucesso');
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Erro ao fazer upload do comprovante');
                }
              } catch (error: any) {
                console.error('Erro no upload do comprovante:', error);
                toast.error('Erro ao enviar comprovante: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (boletoFileForm) {
              try {
                const formDataUpload = new FormData();
                formDataUpload.append('boleto', boletoFileForm);
                const response = await fetch(`/api/contas-financeiro/receber/${contaSelecionada.id}/boleto`, {
                  method: 'POST',
                  body: formDataUpload,
                });
                if (response.ok) {
                  toast.success('Boleto enviado com sucesso');
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Erro ao fazer upload do boleto');
                }
              } catch (error: any) {
                console.error('Erro no upload do boleto:', error);
                toast.error('Erro ao enviar boleto: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (notaFiscalFileForm) {
              try {
                const formDataUpload = new FormData();
                formDataUpload.append('nota_fiscal', notaFiscalFileForm);
                const response = await fetch(`/api/contas-financeiro/receber/${contaSelecionada.id}/nota-fiscal`, {
                  method: 'POST',
                  body: formDataUpload,
                });
                if (response.ok) {
                  toast.success('Nota fiscal enviada com sucesso');
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Erro ao fazer upload da nota fiscal');
                }
              } catch (error: any) {
                console.error('Erro no upload da nota fiscal:', error);
                toast.error('Erro ao enviar nota fiscal: ' + (error.message || 'Erro desconhecido'));
              }
            }

            setDialogOpen(false);
            setComprovanteFileForm(null);
            setBoletoFileForm(null);
            setNotaFiscalFileForm(null);
            carregarContas();
            return;
          }
          
          // Se não há alterações e não há arquivos, apenas fechar o diálogo
          setDialogOpen(false);
          return;
        } else {
          // Edição de conta não parcelada
          const contratoIdParaSalvar = formData.contrato_id && formData.contrato_id.trim() !== '' 
            ? extrairContratoId(formData.contrato_id) 
            : null;
          const contratoEtapaParaSalvar = formData.contrato_id ? extrairEtapa(formData.contrato_id) : null;
          const contratoTipo = contratoIdParaSalvar ? (contratosAberto.find(c => c.id === contratoIdParaSalvar)?.tipo_contrato ?? 'fotovoltaico') : undefined;
          const dadosParaSalvarTemp = {
            descricao: formData.descricao,
            valor: valor!,
            data_vencimento: formData.data_vencimento,
            categoria: formData.categoria,
            status,
            cliente: formData.cliente && formData.cliente.trim() !== '' ? formData.cliente : null,
            contrato_id: contratoIdParaSalvar,
            contrato_etapa: contratoEtapaParaSalvar,
            contrato_tipo: contratoTipo,
            observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
            tipo_pagamento: (formData.tipo_pagamento || undefined) as 'avista' | 'parcelado' | undefined,
            forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
            ...(status === 'recebido' && contaSelecionada?.data_recebimento ? { data_recebimento: contaSelecionada.data_recebimento } : {}),
          };
          await financeiroService.atualizarContaReceber(contaSelecionada.id, dadosParaSalvarTemp);
          contaId = contaSelecionada.id;
          toast.success('Conta atualizada com sucesso');
        }
      } else {
        const empresaDoLancamento = exigirEmpresa();
        if (!empresaDoLancamento) return;
        if (formData.tipo_pagamento === 'parcelado' && formData.quantidade_parcelas) {
          // Criar múltiplas contas para pagamento parcelado
          // O valor informado é o valor de cada parcela, não o valor total
          if (valor === null) {
            toast.error('Valor é obrigatório para pagamento parcelado');
            return;
          }
          const idParcelamento = crypto.randomUUID();
          const quantidadeParcelas = formData.quantidade_parcelas;
          const valorParcela = valor; // O valor informado já é o valor de cada parcela
          const dataVencimentoInicial = parseDateLocal(formData.data_vencimento);
          
          const contasCriadas: ContaReceber[] = [];
          
          for (let i = 1; i <= quantidadeParcelas; i++) {
            const dataVencimentoParcela = addMonths(dataVencimentoInicial, i - 1);
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            
            let statusParcela: 'pendente' | 'recebido' | 'vencido' = 'pendente';
            if (dataVencimentoParcela < hoje) {
              statusParcela = 'vencido';
            }
            
            const contratoIdParaSalvar = formData.contrato_id && formData.contrato_id.trim() !== '' 
              ? extrairContratoId(formData.contrato_id) 
              : null;
            const contratoEtapaParaSalvar = formData.contrato_id ? extrairEtapa(formData.contrato_id) : null;
            const contratoTipo = contratoIdParaSalvar ? (contratosAberto.find(c => c.id === contratoIdParaSalvar)?.tipo_contrato ?? 'fotovoltaico') : undefined;
            const dadosParcela = {
              descricao: formData.descricao, // Descrição sem sufixo de parcela
              empresa_id: empresaDoLancamento,
              valor: valorParcela, // Valor da parcela (mesmo valor para todas)
              data_vencimento: format(dataVencimentoParcela, 'yyyy-MM-dd'),
              categoria: formData.categoria,
              status: statusParcela,
              cliente: formData.cliente && formData.cliente.trim() !== '' ? formData.cliente : null,
              contrato_id: contratoIdParaSalvar,
              contrato_etapa: contratoEtapaParaSalvar,
              contrato_tipo: contratoTipo,
              observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
              tipo_pagamento: 'parcelado' as const,
              forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
              quantidade_parcelas: quantidadeParcelas,
              numero_parcela: i,
              id_parcelamento: idParcelamento,
            };
            
            const contaCriada = await financeiroService.criarContaReceber(dadosParcela);
            contasCriadas.push(contaCriada);
          }
          
          contaId = contasCriadas[0].id; // Usar o ID da primeira parcela para uploads
          const valorTotal = valorParcela * quantidadeParcelas;
          toast.success(`${quantidadeParcelas} parcelas de R$ ${valorParcela.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} criadas (Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`);
        } else {
          // Criar conta única (à vista)
          // Para contas a receber, valor é obrigatório
          if (valor === null) {
            toast.error('Valor é obrigatório');
            return;
          }
          const contratoIdParaSalvar = formData.contrato_id && formData.contrato_id.trim() !== '' 
            ? extrairContratoId(formData.contrato_id) 
            : null;
          const contratoEtapaParaSalvar = formData.contrato_id ? extrairEtapa(formData.contrato_id) : null;
          const contratoTipo = contratoIdParaSalvar ? (contratosAberto.find(c => c.id === contratoIdParaSalvar)?.tipo_contrato ?? 'fotovoltaico') : undefined;
          const dadosParaSalvarTemp = {
            descricao: formData.descricao,
            empresa_id: empresaDoLancamento,
            valor: valor!,
            data_vencimento: formData.data_vencimento,
            categoria: formData.categoria,
            status,
            cliente: formData.cliente && formData.cliente.trim() !== '' ? formData.cliente : null,
            contrato_id: contratoIdParaSalvar,
            contrato_etapa: contratoEtapaParaSalvar,
            contrato_tipo: contratoTipo,
            observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
            tipo_pagamento: (formData.tipo_pagamento || undefined) as 'avista' | 'parcelado' | undefined,
            forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
          };
          const contaCriada = await financeiroService.criarContaReceber(dadosParaSalvarTemp);
          contaId = contaCriada.id;
          toast.success('Conta criada com sucesso');
        }
      }

      // Fazer upload dos arquivos se houver arquivos selecionados
      if (comprovanteFileForm && contaId) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('comprovante', comprovanteFileForm);

          const response = await fetch(`/api/contas-financeiro/receber/${contaId}/comprovante`, {
            method: 'POST',
            body: formDataUpload,
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erro ao fazer upload do comprovante');
          }
          toast.success('Comprovante enviado com sucesso');
        } catch (error: any) {
          console.error('Erro no upload do comprovante:', error);
          toast.error('Conta salva, mas erro ao enviar comprovante: ' + (error.message || 'Erro desconhecido'));
        }
      }

      if (boletoFileForm && contaId) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('boleto', boletoFileForm);

          const response = await fetch(`/api/contas-financeiro/receber/${contaId}/boleto`, {
            method: 'POST',
            body: formDataUpload,
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erro ao fazer upload do boleto');
          }
          toast.success('Boleto enviado com sucesso');
        } catch (error: any) {
          console.error('Erro no upload do boleto:', error);
          toast.error('Conta salva, mas erro ao enviar boleto: ' + (error.message || 'Erro desconhecido'));
        }
      }

      if (notaFiscalFileForm) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('nota_fiscal', notaFiscalFileForm);

          const response = await fetch(`/api/contas-financeiro/receber/${contaId}/nota-fiscal`, {
            method: 'POST',
            body: formDataUpload,
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erro ao fazer upload da nota fiscal');
          }
          toast.success('Nota fiscal enviada com sucesso');
        } catch (error: any) {
          console.error('Erro no upload da nota fiscal:', error);
          toast.error('Conta salva, mas erro ao enviar nota fiscal: ' + (error.message || 'Erro desconhecido'));
        }
      }

      setDialogOpen(false);
      setComprovanteFileForm(null);
      setBoletoFileForm(null);
      setNotaFiscalFileForm(null);
      carregarContas();
    } catch (error: any) {
      console.error('Erro ao salvar conta:', error);
      console.error('Detalhes do erro:', {
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code,
      });
      const mensagemErro = error?.message || error?.details || error?.hint || error?.error_description || 'Erro ao salvar conta';
      toast.error(`Erro ao salvar conta: ${mensagemErro}`);
    }
  };

  const marcarComoRecebido = async (conta: ContaReceber) => {
    try {
      await financeiroService.atualizarContaReceber(conta.id, {
        status: 'recebido',
        data_recebimento: getCurrentDateLocal(),
      });
      toast.success('Conta marcada como recebida');
      carregarContas();
    } catch (error) {
      console.error('Erro ao marcar como recebido:', error);
      toast.error('Erro ao atualizar conta');
    }
  };

  const desmarcarComoRecebido = async (conta: ContaReceber) => {
    try {
      // Verificar se a conta está vencida para definir o status correto
      const dataVencimento = parseDateLocal(conta.data_vencimento);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      let novoStatus: 'pendente' | 'vencido' = 'pendente';
      if (dataVencimento < hoje) {
        novoStatus = 'vencido';
      }

      await financeiroService.atualizarContaReceber(conta.id, {
        status: novoStatus,
        data_recebimento: null,
      });
      toast.success('Conta desmarcada como recebida');
      carregarContas();
    } catch (error) {
      console.error('Erro ao desmarcar como recebido:', error);
      toast.error('Erro ao atualizar conta');
    }
  };

  const deletarComprovante = async (contaId: string) => {
    try {
      const response = await fetch(`/api/contas-financeiro/receber/${contaId}/comprovante`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao deletar comprovante');
      }

      toast.success('Comprovante deletado com sucesso');
      carregarContas();
      if (contaSelecionada?.id === contaId) {
        const contasAtualizadas = await financeiroService.listarContasReceber();
        const contaAtualizada = contasAtualizadas.find(c => c.id === contaId);
        if (contaAtualizada) {
          setContaSelecionada(contaAtualizada);
        }
      }
    } catch (error: any) {
      console.error('Erro ao deletar comprovante:', error);
      toast.error(error.message || 'Erro ao deletar comprovante');
    }
  };

  const deletarBoleto = async (contaId: string) => {
    try {
      const response = await fetch(`/api/contas-financeiro/receber/${contaId}/boleto`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao deletar boleto');
      }

      toast.success('Boleto deletado com sucesso');
      carregarContas();
      if (contaSelecionada?.id === contaId) {
        const contasAtualizadas = await financeiroService.listarContasReceber();
        const contaAtualizada = contasAtualizadas.find(c => c.id === contaId);
        if (contaAtualizada) {
          setContaSelecionada(contaAtualizada);
        }
      }
    } catch (error: any) {
      console.error('Erro ao deletar boleto:', error);
      toast.error(error.message || 'Erro ao deletar boleto');
    }
  };

  const deletarNotaFiscal = async (contaId: string) => {
    try {
      const response = await fetch(`/api/contas-financeiro/receber/${contaId}/nota-fiscal`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao deletar nota fiscal');
      }

      toast.success('Nota fiscal deletada com sucesso');
      carregarContas();
      if (contaSelecionada?.id === contaId) {
        const contasAtualizadas = await financeiroService.listarContasReceber();
        const contaAtualizada = contasAtualizadas.find(c => c.id === contaId);
        if (contaAtualizada) {
          setContaSelecionada(contaAtualizada);
        }
      }
    } catch (error: any) {
      console.error('Erro ao deletar nota fiscal:', error);
      toast.error(error.message || 'Erro ao deletar nota fiscal');
    }
  };

  const aplicarAlteracoesParcela = async () => {
    if (!contaSelecionada || !dadosParaSalvar) return;
    
    try {
      // Verificar se é parcela
      if (contaSelecionada.tipo_pagamento === 'parcelado' && contaSelecionada.id_parcelamento && contaSelecionada.numero_parcela) {
        // Lógica para parcelas
        const quantidadeParcelasAtual = contaSelecionada.quantidade_parcelas || 0;
        const novaQuantidadeParcelas = dadosParaSalvar.quantidade_parcelas || quantidadeParcelasAtual;
        
        // Preparar dados para atualização
        const dadosParaAtualizar: Partial<ContaReceber> = {
          descricao: dadosParaSalvar.descricao,
          valor: dadosParaSalvar.valor!,
          categoria: dadosParaSalvar.categoria,
          cliente: dadosParaSalvar.cliente,
          contrato_id: dadosParaSalvar.contrato_id,
          contrato_etapa: dadosParaSalvar.contrato_etapa,
          contrato_tipo: dadosParaSalvar.contrato_tipo,
          observacoes: dadosParaSalvar.observacoes,
          forma_pagamento: dadosParaSalvar.forma_pagamento,
          quantidade_parcelas: novaQuantidadeParcelas,
        };

        // Se está editando apenas a parcela atual, atualizar também data_vencimento e status
        if (editarEscopo === 'apenas') {
          dadosParaAtualizar.data_vencimento = dadosParaSalvar.data_vencimento;
          dadosParaAtualizar.status = dadosParaSalvar.status;
          if (dadosParaSalvar.status === 'recebido' && dadosParaSalvar.data_recebimento) {
            dadosParaAtualizar.data_recebimento = dadosParaSalvar.data_recebimento;
          }
        }

        // Aplicar atualizações conforme escopo
        await financeiroService.atualizarParcelasReceber(
          contaSelecionada.id_parcelamento!,
          dadosParaAtualizar,
          editarEscopo,
          contaSelecionada.numero_parcela
        );

        // Se escopo é 'todas' ou 'futuras' e data_vencimento mudou, atualizar datas das parcelas
        if ((editarEscopo === 'todas' || editarEscopo === 'futuras') && dadosParaSalvar.data_vencimento !== contaSelecionada.data_vencimento) {
          const parcelas = await financeiroService.listarParcelasReceber(contaSelecionada.id_parcelamento!);
          const dataVencimentoInicial = parseDateLocal(dadosParaSalvar.data_vencimento);
          
          for (const parcela of parcelas) {
            if (parcela.numero_parcela) {
              if (editarEscopo === 'todas' || (editarEscopo === 'futuras' && parcela.numero_parcela >= contaSelecionada.numero_parcela!)) {
                const parcelaReferencia = editarEscopo === 'todas' ? 1 : contaSelecionada.numero_parcela!;
                const diferencaParcelas = parcela.numero_parcela - parcelaReferencia;
                const novaDataVencimento = addMonths(dataVencimentoInicial, diferencaParcelas);
                const hoje = new Date();
                hoje.setHours(0, 0, 0, 0);
                
                let novoStatus: 'pendente' | 'recebido' | 'vencido' = parcela.status;
                if (novaDataVencimento < hoje && parcela.status === 'pendente') {
                  novoStatus = 'vencido';
                }

                await financeiroService.atualizarContaReceber(parcela.id, {
                  data_vencimento: format(novaDataVencimento, 'yyyy-MM-dd'),
                  status: novoStatus,
                });
              }
            }
          }
        }
      }

      // Fazer upload dos arquivos se houver
      const contaIdParaUpload = contaSelecionada.id;
      
      if (comprovanteFileForm) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('comprovante', comprovanteFileForm);
          const response = await fetch(`/api/contas-financeiro/receber/${contaIdParaUpload}/comprovante`, {
            method: 'POST',
            body: formDataUpload,
          });
          if (response.ok) {
            toast.success('Comprovante enviado com sucesso');
          }
        } catch (error: any) {
          console.error('Erro no upload do comprovante:', error);
        }
      }

      if (boletoFileForm) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('boleto', boletoFileForm);
          const response = await fetch(`/api/contas-financeiro/receber/${contaIdParaUpload}/boleto`, {
            method: 'POST',
            body: formDataUpload,
          });
          if (response.ok) {
            toast.success('Boleto enviado com sucesso');
          }
        } catch (error: any) {
          console.error('Erro no upload do boleto:', error);
        }
      }

      if (notaFiscalFileForm) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('nota_fiscal', notaFiscalFileForm);
          const response = await fetch(`/api/contas-financeiro/receber/${contaIdParaUpload}/nota-fiscal`, {
            method: 'POST',
            body: formDataUpload,
          });
          if (response.ok) {
            toast.success('Nota fiscal enviada com sucesso');
          }
        } catch (error: any) {
          console.error('Erro no upload da nota fiscal:', error);
        }
      }

      setEditarEscopoDialogOpen(false);
      setDadosParaSalvar(null);
      setEditarEscopo('apenas');
      
      // Fechar diálogo e recarregar
      setDialogOpen(false);
      setComprovanteFileForm(null);
      setBoletoFileForm(null);
      setNotaFiscalFileForm(null);
      
      const mensagemEscopo = editarEscopo === 'apenas' ? 'esta parcela' : editarEscopo === 'futuras' ? 'as parcelas futuras' : 'todas as parcelas';
      toast.success(`Alterações aplicadas em ${mensagemEscopo} com sucesso`);
      carregarContas();
    } catch (error: any) {
      console.error('Erro ao aplicar alterações:', error);
      toast.error(`Erro ao aplicar alterações: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const deletarConta = async () => {
    if (!contaSelecionada) return;
    try {
      if (contaSelecionada.tipo_pagamento === 'parcelado' && contaSelecionada.id_parcelamento && contaSelecionada.numero_parcela) {
        if (deletarOpcao === 'todas') {
          // Deletar todas as parcelas do parcelamento
          await financeiroService.deletarTodasParcelasReceber(contaSelecionada.id_parcelamento);
          toast.success('Todas as parcelas foram deletadas com sucesso');
        } else if (deletarOpcao === 'futuras') {
          // Deletar a parcela atual e as parcelas futuras
          await financeiroService.deletarParcelasFuturasReceber(contaSelecionada.id_parcelamento, contaSelecionada.numero_parcela);
          const parcelasFuturas = contaSelecionada.quantidade_parcelas! - contaSelecionada.numero_parcela + 1;
          toast.success(`${parcelasFuturas} parcela(s) deletada(s) com sucesso`);
        } else {
          // Deletar apenas a parcela atual
          await financeiroService.deletarContaReceber(contaSelecionada.id);
          toast.success('Conta deletada com sucesso');
        }
      } else {
        // Deletar conta não parcelada
        await financeiroService.deletarContaReceber(contaSelecionada.id);
        toast.success('Conta deletada com sucesso');
      }
      setDeleteDialogOpen(false);
      setDeletarOpcao('apenas');
      setContaSelecionada(null);
      carregarContas();
    } catch (error) {
      console.error('Erro ao deletar conta:', error);
      toast.error('Erro ao deletar conta');
    }
  };

  const fazerUploadComprovante = async () => {
    if (!contaSelecionada || !comprovanteFile) {
      toast.error('Selecione um arquivo para upload');
      return;
    }

    try {
      setUploadingComprovante(true);

      const formData = new FormData();
      formData.append('comprovante', comprovanteFile);

      // Usar URL relativa - fetchWithAuth adiciona o token automaticamente
      const response = await fetch(`/api/contas-financeiro/receber/${contaSelecionada.id}/comprovante`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao fazer upload');
      }

      toast.success('Comprovante enviado com sucesso');
      setComprovanteDialogOpen(false);
      setComprovanteFile(null);
      carregarContas();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload');
    } finally {
      setUploadingComprovante(false);
    }
  };

  const contasFiltradas = contas.filter(conta => {
    const matchBusca = busca === '' || 
      conta.descricao.toLowerCase().includes(busca.toLowerCase()) ||
      conta.cliente?.toLowerCase().includes(busca.toLowerCase());
    return matchBusca;
  });

  // Agrupar contas por mês/ano baseado na data de vencimento
  const contasPorMes = contasFiltradas.reduce((acc, conta) => {
    const dataVencimento = parseDateLocal(conta.data_vencimento);
    const mesAno = format(startOfMonth(dataVencimento), 'yyyy-MM');
    const mesAnoLabel = format(startOfMonth(dataVencimento), "MMMM 'de' yyyy", { locale: ptBR });
    
    if (!acc[mesAno]) {
      acc[mesAno] = {
        label: mesAnoLabel,
        contas: [],
      };
    }
    acc[mesAno].contas.push(conta);
    return acc;
  }, {} as Record<string, { label: string; contas: ContaReceber[] }>);

  const handleSort = (column: ColunaOrdenacaoContaReceber) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Ordenar os meses do mais recente para o mais antigo
  const mesesOrdenados = Object.keys(contasPorMes).sort((a, b) => b.localeCompare(a));

  // Inicializar mesAtual com o mês mais recente que tem contas quando os dados carregam
  useEffect(() => {
    if (mesesOrdenados.length > 0) {
      const mesMaisRecente = mesesOrdenados[0];
      // Só atualizar se o mês atual não existir nos dados carregados
      if (!contasPorMes[mesAtual]) {
        setMesAtual(mesMaisRecente);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesesOrdenados.join(',')]);

  // Funções de navegação entre meses
  const irParaMesAnterior = () => {
    const dataAtual = parseISO(`${mesAtual}-01`);
    const mesAnterior = format(subMonths(dataAtual, 1), 'yyyy-MM');
    setMesAtual(mesAnterior);
  };

  const irParaProximoMes = () => {
    const dataAtual = parseISO(`${mesAtual}-01`);
    const proximoMes = format(addMonths(dataAtual, 1), 'yyyy-MM');
    setMesAtual(proximoMes);
  };

  const voltarAoMesAtual = () => {
    const mesAtualFormatado = format(startOfMonth(new Date()), 'yyyy-MM');
    setMesAtual(mesAtualFormatado);
  };

  // Verificar se está no mês atual
  const estaNoMesAtual = () => {
    const mesAtualFormatado = format(startOfMonth(new Date()), 'yyyy-MM');
    return mesAtual === mesAtualFormatado;
  };

  // Verificar limites de navegação (permitir navegação livre, mas podemos desabilitar se necessário)
  // Por enquanto, permitimos navegação livre entre todos os meses

  // Obter o grupo do mês atual
  const grupoAtual = contasPorMes[mesAtual];
  const contasOrdenadas = grupoAtual
    ? ordenarContasReceber(grupoAtual.contas, sortColumn, sortDirection)
    : [];
  const mesAtualLabel = grupoAtual 
    ? format(parseISO(`${mesAtual}-01`), "MMMM/yyyy", { locale: ptBR })
    : format(parseISO(`${mesAtual}-01`), "MMMM/yyyy", { locale: ptBR });

  // Obter mês e ano atual para o seletor
  const dataAtual = parseISO(`${mesAtual}-01`);
  const mesSelecionado = dataAtual.getMonth();
  const anoSelecionado = dataAtual.getFullYear();

  // Gerar lista de anos (últimos 10 anos até próximos 2 anos)
  const anoAtual = new Date().getFullYear();
  const anosDisponiveis = Array.from({ length: 13 }, (_, i) => anoAtual - 10 + i);

  // Meses em português
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Função para selecionar mês/ano
  const selecionarMesAno = (mes: number, ano: number) => {
    const novoMes = format(new Date(ano, mes, 1), 'yyyy-MM');
    setMesAtual(novoMes);
    setPopoverAberto(false);
  };

  // Verifica se uma conta possui arquivos anexados
  const temArquivosAnexados = (conta: ContaReceber): boolean => {
    return !!(
      conta.comprovante_url ||
      conta.boleto_url ||
      conta.nota_fiscal_url
    );
  };

  // Abre o dialog de documentos
  const abrirDialogDocumentos = (conta: ContaReceber) => {
    setContaParaDocumentos(conta);
    setDocumentosDialogOpen(true);
  };

  // Função para fazer download de um arquivo
  const baixarArquivo = async (url: string, nomeArquivo: string) => {
    try {
      // Tenta fazer download direto da URL pública
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = nomeArquivo || 'arquivo';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Download iniciado');
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      toast.error('Erro ao baixar arquivo. Tente novamente.');
    }
  };

  // Função para visualizar um arquivo em nova aba
  const visualizarArquivo = (url: string) => {
    window.open(url, '_blank');
  };

  // Função para baixar comprovantes, boletos e notas fiscais em um único ZIP
  const baixarDocumentosUnificadosZip = async () => {
    if (!grupoAtual || !grupoAtual.contas.length) {
      toast.error('Nenhuma conta encontrada para o mês selecionado');
      return;
    }

    const contasComDocumentos = grupoAtual.contas.filter(conta =>
      (conta.comprovante_url && conta.comprovante_url.trim() !== '') ||
      (conta.boleto_url && conta.boleto_url.trim() !== '') ||
      (conta.nota_fiscal_url && conta.nota_fiscal_url.trim() !== '')
    );

    if (contasComDocumentos.length === 0) {
      toast.error('Nenhum documento encontrado para o mês selecionado');
      return;
    }

    try {
      setBaixandoDocumentos(true);
      const zip = new JSZip();
      let arquivosAdicionados = 0;

      const fetchAndAdd = async (url: string, nomeNoZip: string) => {
        const response = await fetch(url, { method: 'GET', headers: { 'Cache-Control': 'no-cache' } });
        if (response.ok) {
          const blob = await response.blob();
          zip.file(nomeNoZip, blob);
          arquivosAdicionados++;
        }
      };

      for (const conta of contasComDocumentos) {
        const baseNome = conta.descricao.replace(/[^a-zA-Z0-9]/g, '_');

        if (conta.comprovante_url && conta.comprovante_url.trim() !== '') {
          try {
            const nomeArquivo = conta.comprovante_nome_arquivo || `comprovante_${conta.id}.pdf`;
            await fetchAndAdd(conta.comprovante_url, `Comprovantes/${baseNome}_${conta.id}_${nomeArquivo}`);
          } catch (error) {
            console.error(`Erro ao processar comprovante da conta ${conta.id}:`, error);
          }
        }

        if (conta.boleto_url && conta.boleto_url.trim() !== '') {
          try {
            const nomeArquivo = conta.boleto_nome_arquivo || `boleto_${conta.id}.pdf`;
            await fetchAndAdd(conta.boleto_url, `Boletos/${baseNome}_${conta.id}_${nomeArquivo}`);
          } catch (error) {
            console.error(`Erro ao processar boleto da conta ${conta.id}:`, error);
          }
        }

        if (conta.nota_fiscal_url && conta.nota_fiscal_url.trim() !== '') {
          try {
            const nomeArquivo = conta.nota_fiscal_nome_arquivo || `nota_fiscal_${conta.id}.pdf`;
            await fetchAndAdd(conta.nota_fiscal_url, `Notas_Fiscais/${baseNome}_${conta.id}_${nomeArquivo}`);
          } catch (error) {
            console.error(`Erro ao processar nota fiscal da conta ${conta.id}:`, error);
          }
        }
      }

      if (arquivosAdicionados === 0) {
        toast.error('Não foi possível baixar nenhum documento');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      const nomeMes = format(parseISO(`${mesAtual}-01`), 'yyyy-MM');
      link.download = `documentos_${nomeMes}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${arquivosAdicionados} documento(s) baixado(s) com sucesso`);
    } catch (error) {
      console.error('Erro ao baixar documentos:', error);
      toast.error('Erro ao baixar documentos. Tente novamente.');
    } finally {
      setBaixandoDocumentos(false);
    }
  };

  // Verificar se há arquivos anexados no mês atual
  const temArquivosNoMes = () => {
    if (!grupoAtual || !grupoAtual.contas.length) return false;
    
    return grupoAtual.contas.some(conta => 
      (conta.comprovante_url && conta.comprovante_url.trim() !== '') ||
      (conta.boleto_url && conta.boleto_url.trim() !== '') ||
      (conta.nota_fiscal_url && conta.nota_fiscal_url.trim() !== '')
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'recebido':
        return <Badge variant="default" className="bg-success">Recebido</Badge>;
      case 'vencido':
        return <Badge variant="destructive">Vencido</Badge>;
      default:
        return <Badge variant="secondary">Em Aberto</Badge>;
    }
  };

  return (
    <div className="space-y-4 min-[990px]:space-y-6">
      <div className="flex flex-col min-[990px]:flex-row min-[990px]:items-center min-[990px]:justify-between gap-3">
        <div>
          <h2 className="text-xl min-[990px]:text-2xl font-bold">Contas a Receber</h2>
          <p className="text-xs min-[990px]:text-sm text-muted-foreground">Gerencie suas receitas e valores a receber</p>
        </div>
        <Button 
          onClick={() => abrirDialog()} 
          className="w-full min-[990px]:w-auto gap-2 h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
        >
          <Plus className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4" />
          {isMobile ? 'Nova' : 'Nova Transação'}
        </Button>
      </div>

      <Card>
        <CardHeader className="p-3 min-[990px]:p-6">
          <div className="flex flex-col min-[990px]:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={isMobile ? "Buscar..." : "Buscar por descrição ou cliente..."}
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10 w-full h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
                />
              </div>
            </div>
            {isMobile ? (
              <Drawer>
                <DrawerTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Filter className="h-4 w-4 mr-2" />
                    Filtrar
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>Filtrar Contas</DrawerTitle>
                  </DrawerHeader>
                  <div className="p-4 space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Status</label>
                      <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          <SelectItem value="pendente">Em Aberto</SelectItem>
                          <SelectItem value="recebido">Recebidas</SelectItem>
                          <SelectItem value="vencido">Vencidas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </DrawerContent>
              </Drawer>
            ) : (
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger className="w-full min-[990px]:w-[180px] h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm">
                  <Filter className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Em Aberto</SelectItem>
                  <SelectItem value="recebido">Recebidas</SelectItem>
                  <SelectItem value="vencido">Vencidas</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 min-[990px]:p-6">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : contasFiltradas.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              Nenhuma conta encontrada
            </div>
          ) : mesesOrdenados.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              Nenhuma conta encontrada para os filtros selecionados
            </div>
          ) : (
            <div className="space-y-4">
              {/* Navegação entre meses */}
              <div className="space-y-3 border-b pb-4">
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={irParaMesAnterior}
                    className="h-9 w-9 min-[990px]:h-10 min-[990px]:w-10"
                  >
                    <ChevronLeft className="h-4 w-4 min-[990px]:h-5 min-[990px]:w-5" />
                  </Button>
                  
                  <Popover open={popoverAberto} onOpenChange={setPopoverAberto}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="text-center hover:bg-accent cursor-pointer group px-4 min-[990px]:px-8"
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="text-base min-[990px]:text-xl font-semibold capitalize">
                            {mesAtualLabel}
                          </h3>
                          <Calendar className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-4" align="center">
                      <div className="space-y-4">
                        <div className="text-sm font-medium text-center">Selecione o Mês</div>
                        <div className="grid grid-cols-3 gap-2">
                          {meses.map((mes, index) => (
                            <Button
                              key={index}
                              variant={mesSelecionado === index ? "default" : "outline"}
                              size="sm"
                              onClick={() => selecionarMesAno(index, anoSelecionado)}
                              className="text-xs"
                            >
                              {mes.substring(0, 3)}
                            </Button>
                          ))}
                        </div>
                        <div className="border-t pt-4">
                          <div className="text-sm font-medium mb-2 text-center">Ano</div>
                          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                            {anosDisponiveis.map((ano) => (
                              <Button
                                key={ano}
                                variant={anoSelecionado === ano ? "default" : "outline"}
                                size="sm"
                                onClick={() => selecionarMesAno(mesSelecionado, ano)}
                                className="text-xs"
                              >
                                {ano}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={irParaProximoMes}
                    className="h-9 w-9 min-[990px]:h-10 min-[990px]:w-10"
                  >
                    <ChevronRight className="h-4 w-4 min-[990px]:h-5 min-[990px]:w-5" />
                  </Button>
                  
                  {!estaNoMesAtual() && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={voltarAoMesAtual}
                          className="h-9 w-9 min-[990px]:h-10 min-[990px]:w-10"
                        >
                          <Home className="h-4 w-4 min-[990px]:h-5 min-[990px]:w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Voltar ao mês atual</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                
                {grupoAtual && grupoAtual.contas.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 min-[990px]:flex min-[990px]:items-center min-[990px]:justify-center min-[990px]:gap-4 text-xs min-[990px]:text-sm">
                    <div className="col-span-2 min-[990px]:col-span-1 p-3 bg-muted rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-semibold text-foreground ml-2">
                        R$ {grupoAtual.contas.reduce((acc, c) => acc + c.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-3 bg-success/10 rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Recebidas:</span>
                      <span className="font-semibold text-success ml-2">
                        R$ {grupoAtual.contas.filter(c => c.status === 'recebido').reduce((acc, c) => acc + c.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Em Aberto:</span>
                      <span className="font-semibold text-primary ml-2">
                        R$ {grupoAtual.contas.filter(c => c.status === 'pendente' || c.status === 'vencido').reduce((acc, c) => acc + c.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Lista de contas - Cards no mobile, Tabela no desktop */}
              {grupoAtual && grupoAtual.contas.length > 0 ? (
                isMobile ? (
                  // VERSÃO MOBILE: Cards estilo app financeiro
                  <div className="space-y-3">
                    {contasOrdenadas.map((conta) => (
                      <Card 
                        key={conta.id}
                        className="cursor-pointer hover:bg-accent transition-colors"
                        onClick={() => abrirDialog(conta)}
                      >
                        <CardContent className="p-4">
                          {/* Header do card */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-base truncate">
                                {conta.descricao}
                              </h3>
                              <p className="text-xs text-muted-foreground mt-1">
                                {conta.categoria}
                              </p>
                            </div>
                            {/* Status badge */}
                            <div className="ml-2 flex-shrink-0">
                              {getStatusBadge(conta.status)}
                            </div>
                          </div>

                          {/* Valor em destaque */}
                          <div className="mb-3">
                            <p className="text-2xl font-bold">
                              R$ {conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>

                          {/* Informações secundárias em grid */}
                          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>{format(parseDateLocal(conta.data_vencimento), "dd/MM/yyyy")}</span>
                            </div>
                            {conta.cliente && (
                              <div className="truncate">
                                {conta.cliente}
                              </div>
                            )}
                            {conta.tipo_pagamento && (
                              <div className="col-span-2">
                                {conta.tipo_pagamento === 'parcelado' && conta.numero_parcela && (
                                  <Badge variant="outline" className="text-xs">
                                    Parcela {conta.numero_parcela}/{conta.quantidade_parcelas}
                                  </Badge>
                                )}
                                {conta.tipo_pagamento === 'avista' && (
                                  <Badge variant="outline" className="text-xs">
                                    À Vista
                                  </Badge>
                                )}
                              </div>
                            )}
                            {conta.forma_pagamento && (
                              <div className="col-span-2 text-xs">
                                Forma: {conta.forma_pagamento}
                              </div>
                            )}
                          </div>

                          {/* Ações rápidas */}
                          <div className="flex items-center justify-between pt-3 border-t">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Recebido?</span>
                              <Switch
                                checked={conta.status === 'recebido'}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    marcarComoRecebido(conta);
                                  } else {
                                    desmarcarComoRecebido(conta);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <ContaDocumentacaoBadge
                                conta={conta}
                                tipoConta="receber"
                                onUpdated={() => carregarContas()}
                              />
                              {temArquivosAnexados(conta) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    abrirDialogDocumentos(conta);
                                  }}
                                >
                                  <Paperclip className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  abrirDialog(conta);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContaSelecionada(conta);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // VERSÃO DESKTOP: Tabela (mantém como está)
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className={`cursor-pointer ${sortColumn === 'descricao' ? 'text-primary' : ''}`} onClick={() => handleSort('descricao')}>
                            Descrição
                            {sortColumn === 'descricao' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'categoria' ? 'text-primary' : ''}`} onClick={() => handleSort('categoria')}>
                            Categoria
                            {sortColumn === 'categoria' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'cliente' ? 'text-primary' : ''}`} onClick={() => handleSort('cliente')}>
                            Cliente
                            {sortColumn === 'cliente' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'data_vencimento' ? 'text-primary' : ''}`} onClick={() => handleSort('data_vencimento')}>
                            Vencimento
                            {sortColumn === 'data_vencimento' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'valor' ? 'text-primary' : ''}`} onClick={() => handleSort('valor')}>
                            Valor
                            {sortColumn === 'valor' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'tipo_pagamento' ? 'text-primary' : ''}`} onClick={() => handleSort('tipo_pagamento')}>
                            Tipo de Pagamento
                            {sortColumn === 'tipo_pagamento' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'forma_pagamento' ? 'text-primary' : ''}`} onClick={() => handleSort('forma_pagamento')}>
                            Forma de Pagamento
                            {sortColumn === 'forma_pagamento' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className={`cursor-pointer ${sortColumn === 'status' ? 'text-primary' : ''}`} onClick={() => handleSort('status')}>
                            Recebido?
                            {sortColumn === 'status' ? (
                              sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                            ) : (
                              <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                            )}
                          </TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contasOrdenadas.map((conta) => (
                          <TableRow key={conta.id}>
                            <TableCell className="font-medium">{conta.descricao}</TableCell>
                            <TableCell>{conta.categoria}</TableCell>
                            <TableCell>{conta.cliente || '-'}</TableCell>
                            <TableCell>
                              {format(parseDateLocal(conta.data_vencimento), "dd/MM/yyyy", { locale: ptBR })}
                            </TableCell>
                            <TableCell className="font-semibold">
                              R$ {conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell>
                              {conta.tipo_pagamento === 'avista' ? (
                                'À Vista'
                              ) : conta.tipo_pagamento === 'parcelado' ? (
                                conta.numero_parcela && conta.quantidade_parcelas ? (
                                  `Parcelado (${conta.numero_parcela}/${conta.quantidade_parcelas})`
                                ) : conta.quantidade_parcelas ? (
                                  `Parcelado (${conta.quantidade_parcelas}x)`
                                ) : (
                                  'Parcelado'
                                )
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {conta.forma_pagamento || '-'}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={conta.status === 'recebido'}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    marcarComoRecebido(conta);
                                  } else {
                                    desmarcarComoRecebido(conta);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <ContaDocumentacaoBadge
                                  conta={conta}
                                  tipoConta="receber"
                                  onUpdated={() => carregarContas()}
                                />
                                {temArquivosAnexados(conta) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => abrirDialogDocumentos(conta)}
                                      >
                                        <Paperclip className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Ver documentos anexados</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => abrirDialog(conta)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setContaSelecionada(conta);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )
              ) : (
                <div className="text-center p-8 text-muted-foreground">
                  Nenhuma conta encontrada para {mesAtualLabel}
                </div>
              )}

              {/* Botão de download unificado */}
              {grupoAtual && grupoAtual.contas.length > 0 && temArquivosNoMes() && (
                <div className="flex justify-center pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={baixarDocumentosUnificadosZip}
                    disabled={baixandoDocumentos}
                    className="w-full min-[990px]:w-auto gap-2 h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
                  >
                    {baixandoDocumentos ? (
                      <>
                        <Loader2 className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 animate-spin" />
                        Baixando...
                      </>
                    ) : (
                      <>
                        <FileArchive className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4" />
                        {isMobile ? 'Documentos (ZIP)' : `Baixar Documentos (${mesAtualLabel})`}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {contaSelecionada ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da conta a receber
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Pagamento Previsto do Contrato: primeiro campo. A identificação do vínculo é somente por este campo. */}
            <div className="space-y-2">
              <Label htmlFor="contrato_id">Pagamento Previsto do Contrato</Label>
              <Select 
                value={formData.contrato_id || undefined} 
                onValueChange={(value) => {
                  if (value === "__none__") {
                    setFormData({ ...formData, contrato_id: '' });
                  } else {
                    const pagamentosDisponiveis = obterPagamentosDisponiveis();
                    const pagamentoSelecionado = pagamentosDisponiveis.find(p => p.id === value);
                    if (pagamentoSelecionado) {
                      setFormData({
                        ...formData,
                        contrato_id: pagamentoSelecionado.id,
                        cliente: formData.cliente || pagamentoSelecionado.contrato_nome,
                        descricao: formData.descricao || pagamentoSelecionado.descricao,
                        valor: formData.valor || pagamentoSelecionado.valor.toString(),
                      });
                    } else {
                      setFormData({ ...formData, contrato_id: value });
                    }
                  }
                }}
                disabled={carregandoContratos}
              >
                <SelectTrigger>
                  <SelectValue placeholder={carregandoContratos ? "Carregando..." : "Selecione um pagamento previsto"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhum</SelectItem>
                  {obterPagamentosDisponiveis().map((pagamento) => (
                    <SelectItem key={pagamento.id} value={pagamento.id}>
                      {pagamento.contrato_nome} ({pagamento.tipo_contrato === 'carregador' ? 'Carregador' : 'Fotovoltaico'}) - {pagamento.descricao} - R$ {pagamento.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Selecione um pagamento previsto do contrato para vincular esta transação. A identificação do vínculo é somente por este campo; descrição e valor podem ser alterados livremente.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição *</Label>
                <Input
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Pagamento cliente"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valor">Valor *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                <Input
                  id="data_vencimento"
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoria *</Label>
                <Select value={formData.categoria} onValueChange={(value) => setFormData({ ...formData, categoria: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.contasReceber.categorias || []).map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo_pagamento">Tipo de Pagamento</Label>
                <Select value={formData.tipo_pagamento} onValueChange={(value) => {
                  const tipoPagamento = value as 'avista' | 'parcelado';
                  setFormData({ 
                    ...formData, 
                    tipo_pagamento: tipoPagamento,
                    quantidade_parcelas: tipoPagamento === 'parcelado' ? formData.quantidade_parcelas : undefined,
                  });
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.contasReceber.tiposPagamento || ['avista', 'parcelado', 'recorrente']).map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo === 'avista' ? 'À Vista' : tipo === 'parcelado' ? 'Parcelado' : tipo === 'recorrente' ? 'Recorrente' : tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.contasReceber.formasPagamento || []).map(forma => (
                      <SelectItem key={forma} value={forma}>{forma}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.tipo_pagamento === 'parcelado' && (
              <div className="space-y-2">
                <Label htmlFor="quantidade_parcelas">Quantidade de Parcelas *</Label>
                <Input
                  id="quantidade_parcelas"
                  type="number"
                  min="1"
                  value={formData.quantidade_parcelas || ''}
                  onChange={(e) => setFormData({ ...formData, quantidade_parcelas: parseInt(e.target.value) || undefined })}
                  placeholder="Ex: 12"
                />
              </div>
            )}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cliente">Cliente</Label>
                <Input
                  id="cliente"
                  value={formData.cliente}
                  onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                  placeholder="Nome do cliente"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Informações adicionais..."
                rows={3}
              />
            </div>

            {/* Campo de Upload de Comprovante */}
            <div className="space-y-2">
              <Label htmlFor="comprovante-form">
                Comprovante de Recebimento
                {contaSelecionada?.comprovante_url && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (Comprovante atual: {contaSelecionada.comprovante_nome_arquivo || 'Ver comprovante'})
                  </span>
                )}
              </Label>
              {contaSelecionada?.comprovante_url && (
                <div className="p-3 border rounded-md bg-muted flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">
                      {contaSelecionada.comprovante_nome_arquivo || 'Comprovante anexado'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(contaSelecionada.comprovante_url, '_blank')}
                    >
                      Ver
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deletarComprovante(contaSelecionada.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="comprovante-form"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
                        return;
                      }
                      setComprovanteFileForm(file);
                    }
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="comprovante-form"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {comprovanteFileForm ? comprovanteFileForm.name : 'Selecionar arquivo'}
                </Label>
                {comprovanteFileForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setComprovanteFileForm(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>

            {/* Campo de Upload de Boleto */}
            <div className="space-y-2">
              <Label htmlFor="boleto-form">
                Boleto
                {contaSelecionada?.boleto_url && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (Boleto atual: {contaSelecionada.boleto_nome_arquivo || 'Ver boleto'})
                  </span>
                )}
              </Label>
              {contaSelecionada?.boleto_url && (
                <div className="p-3 border rounded-md bg-muted flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">
                      {contaSelecionada.boleto_nome_arquivo || 'Boleto anexado'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(contaSelecionada.boleto_url, '_blank')}
                    >
                      Ver
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deletarBoleto(contaSelecionada.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="boleto-form"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
                        return;
                      }
                      setBoletoFileForm(file);
                    }
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="boleto-form"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {boletoFileForm ? boletoFileForm.name : 'Selecionar arquivo'}
                </Label>
                {boletoFileForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBoletoFileForm(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>

            {/* Campo de Upload de Nota Fiscal */}
            <div className="space-y-2">
              <Label htmlFor="nota-fiscal-form">
                Nota Fiscal
                {contaSelecionada?.nota_fiscal_url && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (Nota fiscal atual: {contaSelecionada.nota_fiscal_nome_arquivo || 'Ver nota fiscal'})
                  </span>
                )}
              </Label>
              {contaSelecionada?.nota_fiscal_url && (
                <div className="p-3 border rounded-md bg-muted flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">
                      {contaSelecionada.nota_fiscal_nome_arquivo || 'Nota fiscal anexada'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(contaSelecionada.nota_fiscal_url, '_blank')}
                    >
                      Ver
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => deletarNotaFiscal(contaSelecionada.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  id="nota-fiscal-form"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
                        return;
                      }
                      setNotaFiscalFileForm(file);
                    }
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="nota-fiscal-form"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {notaFiscalFileForm ? notaFiscalFileForm.name : 'Selecionar arquivo'}
                </Label>
                {notaFiscalFileForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setNotaFiscalFileForm(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>
            
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={salvarConta}>
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeletarOpcao('apenas');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas ? (
                <>
                  Esta é uma transação parcelada (Parcela {contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas}). 
                  O que deseja excluir?
                </>
              ) : (
                'Tem certeza que deseja excluir esta conta? Esta ação não pode ser desfeita.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas ? (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="apenas"
                    checked={deletarOpcao === 'apenas'}
                    onChange={() => setDeletarOpcao('apenas')}
                    className="h-4 w-4"
                  />
                  <span>Excluir apenas esta parcela ({contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="futuras"
                    checked={deletarOpcao === 'futuras'}
                    onChange={() => setDeletarOpcao('futuras')}
                    className="h-4 w-4"
                  />
                  <span>Excluir esta parcela e as parcelas futuras ({contaSelecionada.numero_parcela} até {contaSelecionada.quantidade_parcelas} - {contaSelecionada.quantidade_parcelas - contaSelecionada.numero_parcela + 1} parcela(s))</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="deletarOpcao"
                    value="todas"
                    checked={deletarOpcao === 'todas'}
                    onChange={() => setDeletarOpcao('todas')}
                    className="h-4 w-4"
                  />
                  <span>Excluir todas as {contaSelecionada.quantidade_parcelas} parcelas do parcelamento</span>
                </label>
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletarOpcao('apenas')}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deletarConta} className="bg-destructive">
              {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas
                ? (deletarOpcao === 'todas' ? 'Excluir todas as parcelas' 
                   : deletarOpcao === 'futuras' ? 'Excluir parcelas futuras'
                   : 'Excluir esta parcela')
                : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Escopo de Edição para Parcelas */}
      <AlertDialog open={editarEscopoDialogOpen} onOpenChange={(open) => {
        setEditarEscopoDialogOpen(open);
        if (!open) {
          setEditarEscopo('apenas');
          setDadosParaSalvar(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Como aplicar as alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas ? (
                <>
                  Esta é uma transação parcelada (Parcela {contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas}). 
                  Como deseja aplicar as alterações feitas?
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {contaSelecionada?.tipo_pagamento === 'parcelado' && contaSelecionada.numero_parcela && contaSelecionada.quantidade_parcelas && dadosParaSalvar ? (
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="apenas"
                    checked={editarEscopo === 'apenas'}
                    onChange={() => setEditarEscopo('apenas')}
                    className="h-4 w-4"
                  />
                  <span>Apenas esta parcela ({contaSelecionada.numero_parcela}/{contaSelecionada.quantidade_parcelas})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="futuras"
                    checked={editarEscopo === 'futuras'}
                    onChange={() => setEditarEscopo('futuras')}
                    className="h-4 w-4"
                  />
                  <span>Esta parcela e as parcelas futuras ({contaSelecionada.numero_parcela} até {contaSelecionada.quantidade_parcelas})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-accent">
                  <input
                    type="radio"
                    name="editarEscopo"
                    value="todas"
                    checked={editarEscopo === 'todas'}
                    onChange={() => setEditarEscopo('todas')}
                    className="h-4 w-4"
                  />
                  <span>Todas as {contaSelecionada.quantidade_parcelas} parcelas do parcelamento</span>
                </label>
              </div>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setEditarEscopo('apenas');
              setDadosParaSalvar(null);
            }}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={aplicarAlteracoesParcela} className="bg-primary">
              Aplicar Alterações
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Documentos Anexados */}
      <Dialog open={documentosDialogOpen} onOpenChange={setDocumentosDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documentos Anexados</DialogTitle>
            <DialogDescription>
              {contaParaDocumentos && `Documentos da transação: ${contaParaDocumentos.descricao}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {contaParaDocumentos && (
              <>
                {/* Comprovante de Recebimento */}
                {contaParaDocumentos.comprovante_url && (
                  <div className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-primary/10 rounded-md">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {contaParaDocumentos.comprovante_nome_arquivo || 'Comprovante'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => visualizarArquivo(contaParaDocumentos.comprovante_url!)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizar</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => baixarArquivo(
                                contaParaDocumentos.comprovante_url!,
                                contaParaDocumentos.comprovante_nome_arquivo || 'comprovante.pdf'
                              )}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}

                {/* Boleto */}
                {contaParaDocumentos.boleto_url && (
                  <div className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-primary/10 rounded-md">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {contaParaDocumentos.boleto_nome_arquivo || 'Boleto'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => visualizarArquivo(contaParaDocumentos.boleto_url!)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizar</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => baixarArquivo(
                                contaParaDocumentos.boleto_url!,
                                contaParaDocumentos.boleto_nome_arquivo || 'boleto.pdf'
                              )}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}

                {/* Nota Fiscal */}
                {contaParaDocumentos.nota_fiscal_url && (
                  <div className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 bg-primary/10 rounded-md">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {contaParaDocumentos.nota_fiscal_nome_arquivo || 'Nota Fiscal'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => visualizarArquivo(contaParaDocumentos.nota_fiscal_url!)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Visualizar</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => baixarArquivo(
                                contaParaDocumentos.nota_fiscal_url!,
                                contaParaDocumentos.nota_fiscal_nome_arquivo || 'nota_fiscal.pdf'
                              )}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Download</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mensagem quando não há documentos */}
                {!contaParaDocumentos.comprovante_url &&
                  !contaParaDocumentos.boleto_url &&
                  !contaParaDocumentos.nota_fiscal_url && (
                    <div className="text-center p-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum documento anexado a esta transação</p>
                    </div>
                  )}
              </>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setDocumentosDialogOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Upload de Comprovante */}
      <Dialog open={comprovanteDialogOpen} onOpenChange={setComprovanteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Comprovante de Recebimento</DialogTitle>
            <DialogDescription>
              {contaSelecionada && `Conta: ${contaSelecionada.descricao}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {contaSelecionada?.comprovante_url && (
              <div className="p-4 border rounded-md bg-muted">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {contaSelecionada.comprovante_nome_arquivo || 'Comprovante atual'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(contaSelecionada?.comprovante_url, '_blank')}
                  >
                    Ver
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="comprovante">
                Comprovante de Recebimento
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="comprovante"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Arquivo muito grande. Tamanho máximo: 5MB');
                        return;
                      }
                      setComprovanteFile(file);
                    }
                  }}
                  className="hidden"
                />
                <Label
                  htmlFor="comprovante"
                  className="cursor-pointer flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {comprovanteFile ? comprovanteFile.name : 'Selecionar arquivo'}
                </Label>
                {comprovanteFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setComprovanteFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setComprovanteDialogOpen(false);
                setComprovanteFile(null);
              }}>
                Cancelar
              </Button>
              <Button onClick={fazerUploadComprovante} disabled={!comprovanteFile || uploadingComprovante}>
                {uploadingComprovante ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Enviar Comprovante
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

